import 'isomorphic-fetch';

import SplitClient from 'apps/application/split-client';
import { removeWhitespaceFromValues } from 'common/utils';

const LocalSplitClient = new SplitClient();
const sanitizeAddOns = addOns => addOns.filter(addOn => addOn.quantity > 0);

export class ApiClient {
  constructor() {
    this.setCsrfToken();
  }

  setCsrfToken() {
    this.csrfToken = this.csrfToken || this.getCsrfToken();
  }

  getCsrfToken() {
    return (global.document.querySelector('meta[name="csrf-token"]') || {})
      .content;
  }

  makeRequest(url, options) {
    return fetch(url, this.buildRequestOptions(options));
  }

  buildRequestOptions(options = {}) {
    return {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...options.headers,
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.csrfToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  }

  handleResponse(request) {
    return request.then(response => {
      if (response.status >= 400) {
        return response.json().then(json => {
          throw new Error(`Error: ${json.error}`);
        });
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      return response.text();
    }, error => error.message);
  }

  // API calls.

  createAddress = address => {
    const body = removeWhitespaceFromValues(address);
    const request = this.makeRequest('/api/v1/user_addresses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.handleResponse(request);
  };

  updateAddress = address => {
    const body = removeWhitespaceFromValues(address);
    const request = this.makeRequest(`/api/v1/user_addresses/${address.id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return this.handleResponse(request);
  };

  deleteAddress = id => {
    const request = this.makeRequest(`/api/v1/user_addresses/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse(request);
  };

  fetchAddress = id => {
    const request = this.makeRequest(`/api/v1/user_addresses/${id}`);
    return this.handleResponse(request);
  };

  fetchAddresses = () => {
    const request = this.makeRequest('/api/v1/user_addresses');
    return this.handleResponse(request);
  };

  fetchAvailablePods = () => {
    const request = this.makeRequest('/api/v1/pods');
    return this.handleResponse(request);
  };

  fetchSubscription = () => {
    const request = this.makeRequest('/api/v1/subscription');
    return this.handleResponse(request);
  };

  fetchSplitTreatments = (customAttributes = {}) => {
    return LocalSplitClient.allSplitTreatments(customAttributes)
      .then(data => {
        this.splitTreatments = data;
        return data;
      })
      .catch(e => {
        bugsnagClient.notify(`Error loading Split Treatments ${e}`);
      });
  };

  fetchComplianceRestrictedStates = () => {
    const request = this.makeRequest(
      '/api/v1/subscription/compliance_restricted_states'
    );
    return this.handleResponse(request);
  };

  updateSubscription = (subscription, changedFields) => {
    if (changedFields.add_ons) {
      changedFields.add_ons = sanitizeAddOns(changedFields.add_ons);
    }

    const request = this.makeRequest('/api/v1/subscription', {
      method: 'PATCH',
      body: JSON.stringify({
        uuid: subscription.uuid,
        ...changedFields,
      }),
    });
    return this.handleResponse(request);
  };

  cancelSubscription = (subscription, cancelReasons, cancelDetails) => {
    const request = this.makeRequest('/api/v1/subscription/cancel', {
      body: JSON.stringify({
        cancel_details: cancelDetails,
        cancel_reasons: cancelReasons,
      }),
      method: 'POST',
    });
    return this.handleResponse(request);
  };

  fetchRegisteredProducts = () => {
    const request = this.makeRequest('/api/v1/product_registrations');
    return this.handleResponse(request);
  };

  fetchRegisteredProductsWarranty = () => {
    const request = this.makeRequest(
      '/api/v1/product_registrations/warranty_eligible'
    );
    return this.handleResponse(request);
  };

  createProductRegistration = product => {
    const request = this.makeRequest('/api/v1/product_registrations', {
      method: 'POST',
      body: JSON.stringify({
        product: removeWhitespaceFromValues(product),
      }),
    });
    return this.handleResponse(request);
  };

  createSmsSubscription = () => {
    const request = this.makeRequest('/api/v1/sms_subscription', {
      method: 'POST',
      body: null,
    });
    return this.handleResponse(request);
  };

  createSubscriptionQuote = addOns => {
    addOns = sanitizeAddOns(addOns);

    const request = this.makeRequest('/api/v1/subscription_quotes', {
      method: 'POST',
      body: JSON.stringify({ add_ons: addOns }),
    });
    return this.handleResponse(request);
  };

  fetchProfile = () => {
    const request = this.makeRequest('/api/v1/profile');
    return this.handleResponse(request);
  };

  updateProfile = user => {
    const request = this.makeRequest('/api/v1/profile', {
      method: 'PUT',
      body: JSON.stringify({
        user: removeWhitespaceFromValues(user),
      }),
    });
    return this.handleResponse(request);
  };

  fetchOrder = orderNumber => {
    const request = this.makeRequest(`/api/v1/orders/${orderNumber}`);
    return this.handleResponse(request);
  };

  fetchOrders = () => {
    const request = this.makeRequest(`/api/v1/orders`);
    return this.handleResponse(request);
  };

  fetchOrderSummary = () => {
    const request = this.makeRequest('/api/v1/orders/summary');
    return this.handleResponse(request);
  };

  fetchVariants = () => {
    const request = this.makeRequest('/api/v1/variants');
    return this.handleResponse(request);
  };

  cancelOrder = orderNumber => {
    const request = this.makeRequest(`/api/v1/orders/${orderNumber}/cancel`, {
      method: 'POST',
    });
    return this.handleResponse(request);
  };

  changeOrderMembership = value => {
    const request = this.makeRequest('/api/v1/cart/membership', {
      method: 'PUT',
      body: JSON.stringify({ membership_order: value }),
    });
    return this.handleResponse(request);
  };

  updateCartLineItems = (lineItems, isCommitting) => {
    const url = `/api/v1/cart`;
    const data = { line_items: lineItems, committing: isCommitting };

    const request = this.makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    return this.handleResponse(request);
  };

  fetchAgeVerification = () => {
    const request = this.makeRequest(`/api/v1/age_verification`);
    return this.handleResponse(request);
  };

  updateAgeVerification = data => {
    if (data.year && data.month && data.day) {
      data.date_of_birth = [data.year, data.month, data.day].join('-');
    }

    const request = this.makeRequest(`/api/v1/age_verification`, {
      method: 'PUT',
      body: JSON.stringify(removeWhitespaceFromValues(data)),
    });
    return this.handleResponse(request);
  };

  ageVerificationVerify = () => {
    const request = this.makeRequest(`/api/v1/age_verification/verify`, {
      method: 'POST',
      body: '',
    });
    return this.handleResponse(request);
  };

  ageVerificationReview = () => {
    const request = this.makeRequest(`/api/v1/age_verification/review`, {
      method: 'POST',
      body: '',
    });
    return this.handleResponse(request);
  };

  fetchVipDevices = () => {
    const request = this.makeRequest(`/api/vip/devices`, {
      method: 'GET',
    });
    return this.handleResponse(request);
  };

  fetchClaimRequests = () => {
    const request = this.makeRequest('/api/v1/claim_requests');
    return this.handleResponse(request);
  };

  fetchCurrentClaimRequest = () => {
    const request = this.makeRequest('/api/v1/claim_requests/current');
    return this.handleResponse(request);
  };

  fetchClaimRequest = id => {
    const request = this.makeRequest(`/api/v1/claim_requests/${id}`);
    return this.handleResponse(request);
  };

  fetchProcessingClaimItems = () => {
    const request = this.makeRequest('/api/v1/claim_items/processing');
    return this.handleResponse(request);
  };

  createClaimRequest = (product, returnReason) => {
    const request = this.makeRequest('/api/v1/claim_requests', {
      method: 'POST',
      body: JSON.stringify({
        claim_request: {
          claim_item: {
            product_registration_id: product.id,
            return_reason: returnReason,
          },
        },
      }),
    });
    return this.handleResponse(request);
  };

  completeClaimRequest = (claimRequest, address) => {
    const request = this.makeRequest(
      `/api/v1/claim_requests/${claimRequest.id}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({
          claim_request: {
            ship_address_id: address.id,
          },
        }),
      }
    );
    return this.handleResponse(request);
  };

  cancelClaimRequest = claimRequest => {
    const request = this.makeRequest(
      `/api/v1/claim_requests/${claimRequest.id}`,
      {
        method: 'DELETE',
      }
    );
    return this.handleResponse(request);
  };

  fetchBraintreeClientToken = () => {
    const request = this.makeRequest(
      '/solidus_paypal_braintree/client_token.json',
      {
        method: 'POST',
        body: '',
      }
    );

    return this.handleResponse(request);
  };

  fetchPaymentSources = () => {
    const request = this.makeRequest('/api/v1/payment_sources');
    return this.handleResponse(request);
  };

  addPaymentSource = params => {
    const request = this.makeRequest(`/api/v1/payment_sources.json`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  startCheckout = location => {
    const request = this.makeRequest('/api/v1/checkout/start', {
      method: 'POST',
      body: JSON.stringify({
        location,
      }),
    });
    return this.handleResponse(request);
  };

  fetchLegalPageContent = () => {
    const request = this.makeRequest('/api/v1/contentful_entries/legal.json');
    return this.handleResponse(request);
  };
}

const api = new ApiClient();

export default api;



// WEBPACK FOOTER //
// ./app/javascript/api/ApiClient.js